/**
 * Canvas Touch Utilities
 *
 * Shared helpers for making Fabric.js canvases work reliably with touch input.
 * Used by the main Editor canvas and the three Wizard canvas steps
 * (ScaleCalibrator, Step3GardenOutline, Step4HeightDifferences).
 *
 * Background: iPadOS Safari delivers native TouchEvents through Fabric.js'
 * mouse:* event pipeline. Reading `clientX/Y` directly on those events yields
 * `undefined` (coordinates live on `touches[0]`/`changedTouches[0]`), and the
 * browser's default `touch-action` swallows drags for native scroll before
 * Fabric.js sees them. See #320 for the original fix in the Editor.
 */

/**
 * Read pointer coordinates from a PointerEvent or native TouchEvent.
 *
 * Falls back to `changedTouches[0]` when `touches` is empty (e.g. on
 * `touchend`). Returns `{x: undefined, y: undefined}` when nothing is
 * available — callers should treat that as a no-op (NaN-check downstream).
 *
 * @param {PointerEvent | TouchEvent | MouseEvent} evt
 * @returns {{x: number | undefined, y: number | undefined}}
 */
export function getEventCoords(evt) {
  if (typeof TouchEvent !== 'undefined' && evt instanceof TouchEvent) {
    const touch = evt.touches?.[0] ?? evt.changedTouches?.[0];
    if (touch) return { x: touch.clientX, y: touch.clientY };
  }
  return { x: evt.clientX, y: evt.clientY };
}

/**
 * Disable native browser drag-to-scroll on a Fabric.js canvas, so single-finger
 * pans and pinch-zoom reach Fabric's event pipeline. Must run after canvas
 * init (the wrapper/upper/lower DOM nodes only exist after `new Canvas(...)`).
 *
 * Safe to call with a partially-constructed or torn-down canvas — missing
 * elements are silently skipped.
 *
 * @param {import('fabric').Canvas | null | undefined} canvas
 */
export function applyTouchActionNone(canvas) {
  if (canvas?.upperCanvasEl) canvas.upperCanvasEl.style.touchAction = 'none';
  if (canvas?.lowerCanvasEl) canvas.lowerCanvasEl.style.touchAction = 'none';
  if (canvas?.wrapperEl) canvas.wrapperEl.style.touchAction = 'none';
}

/**
 * Attach two-finger pinch-to-zoom to a Fabric.js canvas.
 *
 * Background: Fabric.js dropped the `touch:gesture` event after v5, so the old
 * `canvas.on('touch:gesture', …)` handlers are dead against Fabric 6/7 — the
 * event never fires. This wires pinch directly onto the native `touchmove` of
 * the upper canvas element instead (see #746). Requires `touch-action: none`
 * on the canvas (via `applyTouchActionNone`) so the browser hands us the move.
 *
 * The zoom is anchored on the gesture midpoint, converted from viewport
 * (`clientX/Y`) to **canvas-relative** coordinates via the element's bounding
 * rect — passing raw `clientX/Y` to `zoomToPoint` would anchor on the wrong
 * point whenever the canvas is offset from the page origin (e.g. the editor,
 * which sits right of the sidebar).
 *
 * Safe to call with a missing/torn-down canvas — returns a no-op disposer.
 *
 * @param {import('fabric').Canvas | null | undefined} canvas
 * @param {{minZoom?: number, maxZoom?: number, onZoom?: (zoom: number) => void}} [options]
 * @returns {() => void} disposer that detaches all listeners
 */
export function attachPinchZoom(canvas, { minZoom = 0.1, maxZoom = 5, onZoom } = {}) {
  const upper = canvas?.upperCanvasEl;
  if (!upper) return () => {};

  let lastDistance = 0;

  const onTouchMove = (e) => {
    if ((e.touches?.length ?? 0) !== 2) return;
    const [t1, t2] = e.touches;
    const distance = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
    const rect = upper.getBoundingClientRect();
    const center = {
      x: (t1.clientX + t2.clientX) / 2 - rect.left,
      y: (t1.clientY + t2.clientY) / 2 - rect.top,
    };
    if (lastDistance !== 0) {
      let zoom = canvas.getZoom() * (distance / lastDistance);
      zoom = Math.max(minZoom, Math.min(maxZoom, zoom));
      canvas.zoomToPoint(center, zoom);
      onZoom?.(zoom);
    }
    lastDistance = distance;
    // Prevent the browser from also panning/zooming the page during the gesture.
    e.preventDefault();
  };

  // Reset the reference distance once the gesture ends, so the next pinch
  // starts fresh instead of jumping from a stale distance.
  const onTouchEnd = (e) => {
    if ((e.touches?.length ?? 0) < 2) lastDistance = 0;
  };

  upper.addEventListener('touchmove', onTouchMove, { passive: false });
  upper.addEventListener('touchend', onTouchEnd, { passive: true });
  upper.addEventListener('touchcancel', onTouchEnd, { passive: true });

  return () => {
    upper.removeEventListener('touchmove', onTouchMove);
    upper.removeEventListener('touchend', onTouchEnd);
    upper.removeEventListener('touchcancel', onTouchEnd);
  };
}
